import { WeatherData } from '../types/weather.types';
import { WeatherCache } from './weatherCache';
import { retry } from '../utils/weatherHelpers';

export class WeatherService {
  private static readonly API_URL = 'https://api.open-meteo.com/v1/forecast';

  static async fetchCurrentWeather(latitude: number, longitude: number, useCache: boolean = true): Promise<WeatherData> {
    // Check cache first
    if (useCache) {
      const cached = WeatherCache.getWeather(latitude, longitude);
      if (cached) {
        console.log(`Using cached weather data (${cached.source}, age: ${Math.floor(cached.age / 1000)}s)`);
        return cached.data;
      }
    }

    // Fetch fresh data
    try {
      const weatherData = await retry(
        async () => this.fetchWeatherFromAPI(latitude, longitude),
        { maxAttempts: 3, delay: 1000, backoff: true }
      );

      // Cache the result
      WeatherCache.setWeather(latitude, longitude, weatherData, 'api');

      return weatherData;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(`Weather API unavailable (${errorMessage}).`);

      // Try to return cached data even if expired
      const cached = WeatherCache.getWeather(latitude, longitude);
      if (cached) {
        console.log('Using stale cached weather data');
        return cached.data;
      }

      // Final fallback
      console.warn('Using fallback weather values');
      return this.getFallbackWeather();
    }
  }

  private static async fetchWeatherFromAPI(latitude: number, longitude: number): Promise<WeatherData> {
    const params = new URLSearchParams({
      latitude: latitude.toString(),
      longitude: longitude.toString(),
      current: 'temperature_2m,relative_humidity_2m,apparent_temperature,is_day,precipitation,weather_code,cloud_cover,pressure_msl,wind_speed_10m,visibility',
      hourly: 'temperature_2m,snowfall,precipitation,weather_code',
      daily: 'temperature_2m_max,temperature_2m_min,sunrise,sunset,uv_index_max',
      forecast_days: '1',
      timezone: 'auto',
    });

    const response = await fetch(`${this.API_URL}?${params}`);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    // Get current hour index
    const now = new Date();
    const currentHour = now.getHours();

    // Extract current weather
    const current = data.current;
    const hourly = data.hourly;
    const daily = data.daily;

    const temperature = current.temperature_2m;
    const humidity = current.relative_humidity_2m || 50;
    const feelsLike = current.apparent_temperature || temperature;
    const isDay = current.is_day === 1;
    const precipitation = current.precipitation || 0;
    const weatherCode = current.weather_code || 0;
    const cloudCoverage = (current.cloud_cover || 0) / 100; // Convert to 0-1
    const pressure = current.pressure_msl || 1013;
    const windSpeed = current.wind_speed_10m || 0;

    // Get snowfall from hourly data
    const snowfall = hourly.snowfall?.[currentHour] || 0;

    // Get UV index from daily data
    const uvIndex = daily.uv_index_max?.[0] || 0;

    // Determine weather conditions
    const isSnowing = (snowfall > 0 && temperature < 2) || (temperature < 1 && precipitation > 0 && humidity > 70);
    const isRaining = precipitation > 0 && temperature > 2 && !isSnowing;

    // Calculate weather intensity
    let weatherIntensity = 0;

    if (isSnowing) {
      const snowfallMm = snowfall * 10;
      weatherIntensity = Math.min(snowfallMm / 5, 1);

      if (snowfall === 0 && precipitation > 0) {
        weatherIntensity = Math.min(precipitation / 2, 0.6);
      }

      // Temperature modifiers
      if (temperature < -8) weatherIntensity *= 0.7;
      else if (temperature < -3) weatherIntensity *= 0.9;
      else if (temperature >= -1 && temperature <= 0.5) weatherIntensity *= 1.3;

      // Humidity affects snow quality
      if (humidity > 90) weatherIntensity *= 1.1;
      else if (humidity < 50) weatherIntensity *= 0.8;
    } else if (isRaining) {
      weatherIntensity = Math.min(precipitation / 10, 1);
    } else if (temperature < 0 && humidity > 80) {
      weatherIntensity = 0.1;
    } else if (temperature >= 0 && temperature <= 5 && humidity > 85) {
      weatherIntensity = 0.05;
    }

    // Calculate dew point (simplified formula)
    const dewPoint = temperature - ((100 - humidity) / 5);

    // Get visibility from API (in meters, convert to km)
    // API returns visibility in meters, convert to km and round to 1 decimal
    const visibilityMeters = current.visibility || 10000; // Default 10km if not available
    const visibility = Math.round(visibilityMeters / 100) / 10; // Convert to km with 1 decimal

    return {
      temperature,
      snowfall,
      windSpeed,
      humidity,
      precipitation,
      isSnowing,
      isRaining,
      weatherIntensity,
      cloudCoverage,
      visibility,
      pressure,
      uvIndex,
      feelsLike,
      dewPoint,
      weatherCode,
      isDay,
    };
  }

  private static getFallbackWeather(): WeatherData {
    return {
      temperature: -5,
      snowfall: 0,
      windSpeed: 2,
      humidity: 65,
      precipitation: 0,
      isSnowing: false,
      isRaining: false,
      weatherIntensity: 0.3,
      cloudCoverage: 0.5,
      visibility: 10,
      pressure: 1013,
      uvIndex: 0,
      feelsLike: -7,
      dewPoint: -8,
      weatherCode: 2,
      isDay: true,
    };
  }

  static getSnowConfigFromWeather(weather: WeatherData) {
    const normalizedWindSpeed = Math.min(weather.windSpeed / 40, 1) * 0.5;

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const isTablet = typeof window !== 'undefined' && window.innerWidth >= 768 && window.innerWidth < 1024;
    const sizeScale = isMobile ? 0.6 : isTablet ? 0.8 : 1.0;

    let config = {
      particleCount: Math.floor(50 * sizeScale),
      windSpeed: Math.max(normalizedWindSpeed, 0.01),
      baseSpeed: 0.6,
      baseRadius: 1 * sizeScale,
      maxRadius: 3 * sizeScale,
      radiusMultiplier: 0.5,
      gravityStrength: 0.11,
      swayAmplitude: 1.9,
      opacityMin: 0.4,
      opacityMax: 0.6,
      enableWind: weather.windSpeed > 1,
      enableSway: true,
      enableGradient: true,
    };

    if (weather.temperature <= -10) {
      config.particleCount = Math.floor((30 + Math.random() * 40) * sizeScale);
      config.baseSpeed = 0.9;
      config.swayAmplitude = 1.0;
      config.gravityStrength = 0.15;
      config.opacityMin = 0.5;
      config.opacityMax = 0.7;

      if (weather.humidity > 80) config.particleCount *= 1.3;
    } else if (weather.temperature <= -5) {
      config.particleCount = Math.floor((60 + Math.random() * 60) * sizeScale);
      config.baseSpeed = 0.8;
      config.swayAmplitude = 1.3;
      config.gravityStrength = 0.13;

      if (weather.humidity > 85) {
        config.particleCount *= 1.2;
        config.opacityMax = 0.75;
      }
    } else if (weather.temperature <= 0) {
      config.particleCount = Math.floor((80 + Math.random() * 70) * sizeScale);
      config.baseSpeed = 0.7;
      config.swayAmplitude = 1.6;

      if (weather.humidity > 90) {
        config.baseSpeed *= 0.8;
        config.swayAmplitude *= 1.2;
        config.gravityStrength *= 0.9;
      }
    } else if (weather.temperature <= 5) {
      const humidityFactor = weather.humidity / 100;
      config.particleCount = Math.floor((40 + Math.random() * 40) * humidityFactor * sizeScale);
      config.baseSpeed = 0.5;
      config.baseRadius = 0.8 * sizeScale;
      config.maxRadius = 2.4 * sizeScale;
      config.swayAmplitude = 2.2;

      if (weather.humidity > 85) {
        config.particleCount *= 1.5;
        config.opacityMax = 0.6;
      }
    } else {
      const humidityFactor = Math.max(weather.humidity / 100 - 0.3, 0);
      config.particleCount = Math.floor((20 + Math.random() * 30) * humidityFactor * sizeScale);
      config.baseSpeed = 0.4;
      config.baseRadius = 0.6 * sizeScale;
      config.maxRadius = 1.8 * sizeScale;
      config.swayAmplitude = 2.5;
    }

    if (weather.windSpeed > 20) {
      config.swayAmplitude *= 1.5;
      config.windSpeed = Math.min(normalizedWindSpeed * 1.5, 0.8);
      config.particleCount = Math.floor(config.particleCount * 0.8);
    } else if (weather.windSpeed > 10) {
      config.swayAmplitude *= 1.2;
      config.windSpeed = normalizedWindSpeed * 1.2;
    } else if (weather.windSpeed < 2) {
      config.swayAmplitude *= 0.8;
      config.windSpeed *= 0.5;
    }

    if (weather.isSnowing) {
      const snowfallIntensity = Math.min(weather.snowfall / 15, 1);
      const intensityMultiplier = 1 + snowfallIntensity * 3;

      config.particleCount = Math.floor(Math.max(config.particleCount, 150 * sizeScale) * intensityMultiplier);

      if (weather.temperature < -5) {
        config.baseSpeed *= 1.1;
        config.gravityStrength *= 0.9;
        config.swayAmplitude *= 0.9;
      } else if (weather.temperature > -1) {
        config.baseSpeed *= 0.7;
        config.gravityStrength *= 0.8;
        config.swayAmplitude *= 1.3;
        config.particleCount = Math.floor(config.particleCount * 0.8);
      }

      config.windSpeed = Math.max(config.windSpeed, normalizedWindSpeed * 0.8);
    }

    const maxParticles = sizeScale < 1 ? 400 : 600;
    config.particleCount = Math.min(Math.max(config.particleCount, 0), maxParticles);

    config.windSpeed = Math.max(0.005, Math.min(config.windSpeed, 1.0));
    config.baseSpeed = Math.max(0.2, Math.min(config.baseSpeed, 2.0));
    config.gravityStrength = Math.max(0.05, Math.min(config.gravityStrength, 0.2));
    config.swayAmplitude = Math.max(0.5, Math.min(config.swayAmplitude, 4.0));

    return config;
  }
}
